
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Drawer,
  Divider,
  CircularProgress,
  Switch,
  FormControlLabel,
  Badge,
  Skeleton,
  Fade,
  Autocomplete,
  TableSortLabel,
  Paper,
  Popover,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Archive as ArchiveIcon,
  DirectionsBike as BikeIcon,
  FilterList as FilterIcon,
  Upload as UploadIcon,
  // Battery20 as BatteryLowIcon,
  // Battery80 as BatteryGoodIcon,
  // BatteryFull as BatteryFullIcon,
  LocationOn as LocationIcon,
  Speed as SpeedIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  ClearAll as ClearIcon,
  Close as CloseIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import * as XLSX from 'xlsx';
import { vehicleService, normalizeOperationalStatus } from '../services/vehicleService';
import { hubService, HubResponse } from '../services/hubService';
import { usePermissions } from '../hooks/usePermissions';

// Enhanced Types
interface Vehicle {
  id: string;
  make: string;
  model: { name: string; displayName: string; };
  year: number;
  vin: string;
  registrationNumber: string;
  color: string;
  oem: string; // OEM display name for filtering and display
  oemId: string; // OEM ID for reference
  status: string; // Transformed lowercase status for filtering
  rawOperationalStatus: string; // Original backend operationalStatus
  vehicleStatus?: string; // Derived or backend-supplied vehicleStatus (DEPLOYED/RETURNED)
  batteryLevel?: number;
  range?: number;
  location?: string;
  // city?: string;
  lastService?: string;
  nextService?: string;
  mileage?: number;
  operationalHours?: number;
  averageSpeed?: number;
  efficiency?: number;
  lastUpdated?: string;
  operationalStatusUpdatedBy?: string;
  operationalStatusUpdatedAt?: string;
  assignedDriver?: string;
  totalTrips?: number;
  documents?: Array<{
    id: string;
    name: string;
    type: string;
    url: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface VehicleFilters {
  status?: string;
  vehicleStatus?: string;
  oem?: string;
  year?: string;
  location?: string;
  batteryLevel?: string;
  maintenanceStatus?: string;
  assignedDriver?: string;
  vehicleModel?: string;
  startDate?: string;
  endDate?: string;
  updatedStartDate?: string;
  updatedEndDate?: string;
  hubId?: string;
}

interface SortConfig {
  key: keyof Vehicle | null;
  direction: 'asc' | 'desc';
}

const OPERATIONAL_STATUS_OPTIONS = [
  'Available',
  'Assigned',
  'Assigned - Team Use',
  'Returned',
  'Available - Without(Battery & Charger)',
  'Under Preventive Maintenance',
  'Under Maintenance - Spare Issue',
  'Under Maintenance - External Work',
  'Under Maintenance - IoT Failure',
  'Under Maintenance - Accidental / Insurance Claim',
  'Under Maintenance - Major Repair',
  'Under Recovery',
  'Under Police Custody / FIR',
  'Under Warranty Claim',
  'Under PDI',
  'Asset Missing',
  'To Be Scrapped / Returned to Lessor',
  'Scrapped / Returned to Lessor',
  'Retired',
  'Damaged',
];

const getCurrentUserDisplayName = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return fullName || user.name || user.fullName || user.email || localStorage.getItem('userName') || 'Current User';
  } catch {
    return localStorage.getItem('userName') || 'Current User';
  }
};

const parseDateKey = (value?: string) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateKey = (value: Date | null) => {
  if (!value || Number.isNaN(value.getTime())) return undefined;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const VEHICLE_INVENTORY_STATE_KEY = 'ev91:vehicle-inventory:state';

interface VehicleInventoryStoredState {
  page?: number;
  rowsPerPage?: number;
  searchQuery?: string;
  searchInputValue?: string;
  filters?: VehicleFilters;
  quickFilters?: {
    lowBattery: boolean;
    maintenanceDue: boolean;
    recentlyUpdated: boolean;
  };
}

const getStoredVehicleInventoryState = (): VehicleInventoryStoredState => {
  if (typeof window === 'undefined') return {};

  try {
    const rawState = window.sessionStorage.getItem(VEHICLE_INVENTORY_STATE_KEY);
    return rawState ? JSON.parse(rawState) : {};
  } catch {
    return {};
  }
};

// Advanced Vehicle Inventory Component
const VehicleInventoryPage: React.FC = () => {
  console.log('🚀 VehicleInventory: Advanced component rendered');

  const navigate = useNavigate();
  const { hasCreatePermission, hasUpdatePermission, hasDeletePermission, hasReadPermission } = usePermissions();
  const storedVehicleState = getStoredVehicleInventoryState();

  // Check if user has read access to vehicles - Must check permissions before using other hooks
  const hasVehicleAccess = hasReadPermission('vehicle', 'vehicles');

  // Core State Management - All hooks must be defined before any conditional returns
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [totalVehicles, setTotalVehicles] = useState(0); // Total count from backend
  // Removed VehicleStats and stats state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // const [allCities, setAllCities] = useState<{ id: string; name: string }[]>([]);
  const [allHubs, setAllHubs] = useState<HubResponse[]>([]);
  useEffect(() => {
    const fetchAllHubs = async () => {
      try {
        const hubs = await hubService.getHubs();
        setAllHubs(hubs);
      } catch (err) {
        setAllHubs([]);
      }
    };
    fetchAllHubs();
  }, []);

  // Render permission error if no access - moved after all hooks are defined
  if (!hasVehicleAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to view vehicle inventory. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  // Pagination & Sorting
  const [page, setPage] = useState(storedVehicleState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(storedVehicleState.rowsPerPage || 25);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'updatedAt', direction: 'desc' });

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState(storedVehicleState.searchQuery || '');
  const [filters, setFilters] = useState<VehicleFilters>(storedVehicleState.filters || {});
// Removed city-based hub fetching effect
  const [quickFilters, setQuickFilters] = useState(storedVehicleState.quickFilters || {
    lowBattery: false,
    maintenanceDue: false,
    recentlyUpdated: false,
  });

  // UI State
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  // Use anchorPosition for a fixed popover placement so it doesn't shift with layout changes
  const [filterPopoverPosition, setFilterPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const openFilterPopover = Boolean(filterPopoverPosition);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const refreshInterval = 30000; // 30 seconds

  const handleOpenFilterPopover = () => {
    // Place the popover below the app bar at a consistent location from the right edge
    const top = 72; // adjust if your app bar height changes
    const popoverWidth = 360; // matches the popover width
    const left = Math.max(16, (typeof window !== 'undefined' ? window.innerWidth - popoverWidth - 24 : 800));
    setFilterPopoverPosition({ top, left });
  };
  const handleCloseFilterPopover = () => setFilterPopoverPosition(null);

  const activeFiltersCount = useMemo(() => {
    const c1 = Object.values(filters).filter(Boolean).length;
    const c2 = Object.values(quickFilters).filter(Boolean).length;
    return c1 + c2;
  }, [filters, quickFilters]);

  // Notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Create ref outside of useEffect and before any conditionals
  const isInitialDataLoadRef = useRef(true);

  // Debounced search to prevent API calls on every keystroke
  const [searchInputValue, setSearchInputValue] = useState(
    storedVehicleState.searchInputValue || storedVehicleState.searchQuery || ''
  );

  const [updatingVehicleId, setUpdatingVehicleId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryInitialized, setSummaryInitialized] = useState(false);
  const [summaryStats, setSummaryStats] = useState({
    total: 0,
    deployed: 0,
    returned: 0,
    rtd: 0,
  });

  useEffect(() => {
    window.sessionStorage.setItem(
      VEHICLE_INVENTORY_STATE_KEY,
      JSON.stringify({
        page,
        rowsPerPage,
        searchQuery,
        searchInputValue,
        filters,
        quickFilters,
      }),
    );
  }, [page, rowsPerPage, searchQuery, searchInputValue, filters, quickFilters]);

  // Enhanced Status Colors - mapping backend operationalStatus (normalized lowercased keys) to frontend status
  const statusConfig: Record<string, { color: 'default'|'primary'|'secondary'|'error'|'info'|'success'|'warning'; icon: any; label: string }> = {
    'active': { color: 'success', icon: SuccessIcon, label: 'Active' },
    'available': { color: 'success', icon: SuccessIcon, label: 'Available' },
    'available - without(battery & charger)': { color: 'warning', icon: WarningIcon, label: 'Available - Without Battery/Charger' },
    'assigned': { color: 'info', icon: PendingIcon, label: 'Assigned' },
    'assigned - team use': { color: 'info', icon: PendingIcon, label: 'Assigned - Team Use' },
    'returned': { color: 'secondary', icon: SuccessIcon, label: 'Returned' },
    'under preventive maintenance': { color: 'warning', icon: WarningIcon, label: 'Under Preventive Maintenance' },
    'under maintenance - spare issue': { color: 'warning', icon: WarningIcon, label: 'Under Maintenance - Spare Issue' },
    'under maintenance - extenal work': { color: 'warning', icon: WarningIcon, label: 'Under Maintenance - External Work' },
    'under maintenance - iot failure': { color: 'warning', icon: WarningIcon, label: 'Under Maintenance - IoT Failure' },
    'under maintenance - accidental / insurance claim': { color: 'error', icon: ErrorIcon, label: 'Under Maintenance - Accidental/Insurance' },
    'under maintenance - major repair': { color: 'error', icon: ErrorIcon, label: 'Under Maintenance - Major Repair' },
    'under recovery': { color: 'info', icon: PendingIcon, label: 'Under Recovery' },
    'under police custody / fir': { color: 'error', icon: ErrorIcon, label: 'Under Police Custody / FIR' },
    'under warranty claim': { color: 'secondary', icon: PendingIcon, label: 'Under Warranty Claim' },
    'under pdi': { color: 'info', icon: PendingIcon, label: 'Under PDI' },
    'asset missing': { color: 'error', icon: ErrorIcon, label: 'Asset Missing' },
    'to be scrapped / returned to lessor': { color: 'error', icon: ErrorIcon, label: 'To be Scrapped / Returned to Lessor' },
    'scrapped /  / returned to lessor': { color: 'error', icon: ErrorIcon, label: 'Scrapped / Returned to Lessor' },
    'maintenance': { color: 'warning', icon: WarningIcon, label: 'Maintenance' },
    'under maintenance': { color: 'warning', icon: WarningIcon, label: 'Under Maintenance' },
    'retired': { color: 'error', icon: ErrorIcon, label: 'Retired' },
    'damaged': { color: 'error', icon: ErrorIcon, label: 'Damaged' },
    'unknown': { color: 'default', icon: PendingIcon, label: 'Unknown' },
  };

  // Memoized calculations - with backend pagination, displayedVehicles is just the current vehicles
  const displayedVehicles = useMemo(() => {
    console.log('🔍 Pagination Debug:', {
      totalVehicles: totalVehicles,
      currentPageVehicles: vehicles.length,
      currentPage: page,
      rowsPerPage: rowsPerPage,
      backendPagination: true
    });

    // With backend pagination, we don't need to slice - the backend already returns the right page
    return vehicles;
  }, [vehicles, totalVehicles, page, rowsPerPage]);

  const buildApiFilters = useCallback(() => ({
    search: searchQuery || undefined,
    operationalStatus: filters.status || undefined,
    vehicleStatus: filters.vehicleStatus || undefined,
    hubId: filters.hubId || undefined,
    oemType: filters.oem || undefined,
    location: filters.location || undefined,
    minMileage: quickFilters.lowBattery ? 0 : undefined,
    maxMileage: quickFilters.lowBattery ? 200 : undefined,
    vehicleModel: filters.vehicleModel || undefined,
    serviceStatus: quickFilters.maintenanceDue ? 'Scheduled for Service' : undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    updatedStartDate: filters.updatedStartDate || undefined,
    updatedEndDate: filters.updatedEndDate || undefined,
    ...(filters.year && { year: filters.year }),
  }), [filters, quickFilters, searchQuery]);

  const transformVehicleRecord = useCallback((vehicle: any): Vehicle => {
    const rawStatus = vehicle.operationalStatus || vehicle.status || 'unknown';
    const normalizedStatus = normalizeOperationalStatus(rawStatus);

    return {
      ...vehicle,
      hubId: vehicle.hub?.id,
      make: vehicle.model?.oem?.displayName || vehicle.model?.oem?.name || 'Unknown',
      oem: vehicle.model?.oem?.displayName || vehicle.model?.oem?.name || 'Unknown',
      oemId: vehicle.model?.oem?.id || 'unknown',
      batteryLevel: vehicle.batteryCapacity || Math.floor(Math.random() * 100),
      range: vehicle.maxRange || 0,
      status: normalizedStatus,
      rawOperationalStatus: rawStatus,
      vehicleStatus: vehicle.vehicleStatus || ((String(normalizedStatus).toLowerCase().includes('assigned')) ? 'DEPLOYED' : 'RETURNED'),
      lastUpdated: vehicle.updatedAt,
      operationalStatusUpdatedBy:
        vehicle.lastOperationalStatusUpdatedBy ||
        vehicle.statusHistory?.[0]?.changedBy ||
        '',
      operationalStatusUpdatedAt:
        vehicle.lastOperationalStatusUpdatedAt ||
        vehicle.statusHistory?.[0]?.changeDate ||
        '',
      vin: vehicle.chassisNumber || vehicle.registrationNumber,
      location: vehicle.location || vehicle.hub?.name || vehicle.hub?.city?.displayName || 'Unknown',
    };
  }, []);

  const applyClientSideFilters = useCallback((items: Vehicle[]) => {
    let finalVehicles = items;

    if (filters.vehicleStatus) {
      finalVehicles = finalVehicles.filter((v: any) => String((v.vehicleStatus || '')).toLowerCase() === String(filters.vehicleStatus).toLowerCase());
    }

    if (filters.hubId) {
      finalVehicles = finalVehicles.filter((v: any) => v.hubId === filters.hubId);
    }

    if (filters.updatedStartDate || filters.updatedEndDate) {
      finalVehicles = finalVehicles.filter((v: any) => {
        if (!v.lastUpdated) return false;
        const updated = new Date(v.lastUpdated);

        if (filters.updatedStartDate) {
          const start = new Date(filters.updatedStartDate);
          start.setHours(0, 0, 0, 0);
          if (updated < start) return false;
        }

        if (filters.updatedEndDate) {
          const end = new Date(filters.updatedEndDate);
          end.setHours(23, 59, 59, 999);
          if (updated > end) return false;
        }

        return true;
      });
    }

    return finalVehicles;
  }, [filters.hubId, filters.updatedEndDate, filters.updatedStartDate, filters.vehicleStatus]);

  const loadVehicleSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);

      const apiFilters = buildApiFilters();
      const pageSize = 1000;
      let currentPage = 1;
      let totalPages = 1;
      const summaryVehicles: Vehicle[] = [];

      do {
        const response = await vehicleService.getVehicles(apiFilters, {
          page: currentPage,
          limit: pageSize,
          sortBy: sortConfig.key || 'updatedAt',
          sortOrder: sortConfig.direction || 'desc',
        });

        const rows = ((response as any).vehicles || (response as any).data || []).map(transformVehicleRecord);
        summaryVehicles.push(...applyClientSideFilters(rows));

        totalPages = Number((response as any).pagination?.totalPages || 1);
        currentPage += 1;
      } while (currentPage <= totalPages);

      setSummaryStats({
        total: summaryVehicles.length,
        deployed: summaryVehicles.filter((vehicle) => String(vehicle.vehicleStatus || '').toUpperCase() === 'DEPLOYED').length,
        returned: summaryVehicles.filter((vehicle) => String(vehicle.vehicleStatus || '').toUpperCase() === 'RETURNED').length,
        rtd: summaryVehicles.filter((vehicle) => String(vehicle.rawOperationalStatus || '').toLowerCase() === 'available').length,
      });
      setSummaryInitialized(true);
    } catch (error) {
      console.error('Vehicle summary failed:', error);
    } finally {
      setSummaryLoading(false);
    }
  }, [applyClientSideFilters, buildApiFilters, sortConfig.direction, sortConfig.key, transformVehicleRecord]);

  // Enhanced data loading with retry logic
  const loadVehicles = useCallback(async (showLoader = true) => {
    try {
      console.log('📡 VehicleInventory: Loading vehicles data...');

      if (showLoader) setLoading(true);
      setError(null);

      // Load vehicles with backend pagination instead of client-side
      // Map all filters properly for backend API
      const apiFilters = buildApiFilters();

      // Debug logs for search filter
      console.log('🔍 DEBUG VehicleInventory: Current search query state:', searchQuery);
      if (searchQuery) {
        console.log(`🔍 DEBUG VehicleInventory: Adding search filter: "${searchQuery}"`);
      }

      console.log('🔍 Sending filters to API:', apiFilters);

      const vehiclesResponse = await vehicleService.getVehicles(
        apiFilters,
        {
          page: page + 1, // Backend uses 1-based pagination
          limit: rowsPerPage, // Use actual rows per page for backend pagination
          sortBy: sortConfig.key || 'updatedAt',
          sortOrder: sortConfig.direction || 'desc',
        }
      );

      console.log('✅ VehicleInventory: Vehicles loaded:', vehiclesResponse);

      // Handle different response structures
      const vehiclesArray = (vehiclesResponse as any).vehicles || (vehiclesResponse as any).data || [];

      console.log('📊 Vehicle data debug:', {
        totalVehiclesFromAPI: vehiclesArray.length,
        hasMorePages: vehiclesResponse.pagination?.totalPages > 1,
        currentPage: vehiclesResponse.pagination?.currentPage,
        totalPages: vehiclesResponse.pagination?.totalPages,
        totalItems: vehiclesResponse.pagination?.totalItems
      });

      // Transform the data to match the frontend interface
      const transformedVehicles = vehiclesArray.map(transformVehicleRecord);
      const finalVehicles = applyClientSideFilters(transformedVehicles);

      setVehicles(finalVehicles);
      // When filtering client-side, total should reflect the filtered set
setTotalVehicles(vehiclesResponse.pagination?.totalItems || 0);
      console.log('🚗 Vehicles after transformation and filtering:', {
        totalTransformed: transformedVehicles.length,
        totalFiltered: finalVehicles.length,
        appliedFilters: {
          vehicleStatus: filters.vehicleStatus,
          updatedStartDate: filters.updatedStartDate,
          updatedEndDate: filters.updatedEndDate,
        },
        currentPage: vehiclesResponse.pagination?.currentPage,
        totalPages: vehiclesResponse.pagination?.totalPages,
        first5VehicleIds: finalVehicles.slice(0, 5).map((v: Vehicle) => v.id)
      });

      // Removed VehicleStats and stats logic

    } catch (error) {
      console.error('❌ VehicleInventory: Error loading data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load vehicle data');
      setSnackbar({
        open: true,
        message: 'Failed to load vehicle data. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [applyClientSideFilters, buildApiFilters, filters, page, rowsPerPage, searchQuery, sortConfig.key, sortConfig.direction, transformVehicleRecord]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      console.log('🔄 Auto-refreshing vehicle data...');
      loadVehicles(false);
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, loadVehicles]);

  // Effects
  // Load data on initial render
  useEffect(() => {
    loadVehicles();
  }, []);

  // Reload data when filters, pagination or search query change
  // We don't need to depend on loadVehicles itself, which would cause excessive reloads
  useEffect(() => {
    // Skip the initial render since we already load data above
    if (isInitialDataLoadRef.current) {
      isInitialDataLoadRef.current = false;
      return;
    }

    loadVehicles(false);
  }, [filters, page, rowsPerPage, searchQuery, sortConfig.key, sortConfig.direction]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadVehicleSummary();
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [loadVehicleSummary]);

  // Removed city fetching effect

  // Enhanced Event Handlers
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([loadVehicles(), loadVehicleSummary()]);
  }, [loadVehicles, loadVehicleSummary]);

  const handleDeploymentTicketsToggle = useCallback(() => {
    navigate('/vehicles/deployment-tickets');
  }, [navigate]);

  const handleReturnTicketsToggle = useCallback(() => {
    navigate('/vehicles/return-tickets');
  }, [navigate]);

  // This function updates the input value without triggering API calls
  const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.target.value);
  }, []);

  const handleApplySearch = useCallback(() => {
    setPage(0);
    setSearchQuery(searchInputValue.trim());
  }, [searchInputValue]);

  const handleFilterChange = useCallback((filterType: keyof VehicleFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value || undefined,
    }));
    // reset to first page when filters change to avoid showing empty pages
    setPage(0);
  }, []);

  const handleQuickFilterChange = useCallback((filter: keyof typeof quickFilters, checked: boolean) => {
    setQuickFilters(prev => ({
      ...prev,
      [filter]: checked,
    }));
  }, []);

  const handleSort = useCallback((key: keyof Vehicle) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    window.sessionStorage.removeItem(VEHICLE_INVENTORY_STATE_KEY);
    setFilters({});
    setQuickFilters({
      lowBattery: false,
      maintenanceDue: false,
      recentlyUpdated: false,
    });
    setSearchInputValue('');
    setSearchQuery('');
    setPage(0);
  }, []);
 
  const handlePageChange = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    console.log('📄 Rows per page changing:', {
      oldValue: rowsPerPage,
      newValue: newRowsPerPage,
      resetPage: true
    });
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, [rowsPerPage]);

  // Navigation handlers
  const handleAddVehicle = useCallback(() => {
    navigate('/vehicles/add');
  }, [navigate]);

  const handleEditVehicle = useCallback((vehicleId: string) => {
    navigate(`/vehicles/edit/${vehicleId}`);
  }, [navigate]);

  const handleViewVehicle = useCallback((vehicleId: string) => {
    navigate(`/vehicles/${vehicleId}`); // Now unified to use VehicleProfile
  }, [navigate]);

  // Enhanced vehicle actions
  const handleArchiveVehicle = useCallback(async (vehicleId: string) => {
    try {
      await vehicleService.updateVehicleStatus(vehicleId, 'Retired', 'Archived by admin');
      setSnackbar({
        open: true,
        message: 'Vehicle archived successfully',
        severity: 'success',
      });
      loadVehicles(false);
    } catch (error) {
      console.error('Error archiving vehicle:', error);
      setSnackbar({
        open: true,
        message: 'Failed to archive vehicle',
        severity: 'error',
      });
    }
  }, [loadVehicles]);

  const handleExportVehicles = useCallback(async () => {
    try {
      setExporting(true);

      const apiFilters = buildApiFilters();
      const pageSize = 1000;
      let currentPage = 1;
      let totalPages = 1;
      const exportedVehicles: Vehicle[] = [];

      do {
        const response = await vehicleService.getVehicles(apiFilters, {
          page: currentPage,
          limit: pageSize,
          sortBy: sortConfig.key || 'updatedAt',
          sortOrder: sortConfig.direction || 'desc',
        });

        const rows = ((response as any).vehicles || (response as any).data || []).map(transformVehicleRecord);
        exportedVehicles.push(...applyClientSideFilters(rows));

        totalPages = Number((response as any).pagination?.totalPages || 1);
        currentPage += 1;
      } while (currentPage <= totalPages);

      if (exportedVehicles.length === 0) {
        setSnackbar({
          open: true,
          message: 'No vehicle data available to export',
          severity: 'info',
        });
        return;
      }

      const exportRows = exportedVehicles.map((vehicle) => ({
        'Registration Number': vehicle.registrationNumber || '',
        VIN: vehicle.vin || '',
        Make: vehicle.make || '',
        Model: vehicle.model?.displayName || vehicle.model?.name || '',
        Year: vehicle.year || '',
        'Operational Status': vehicle.rawOperationalStatus || '',
        'Vehicle Status': vehicle.vehicleStatus || '',
        Range: vehicle.range ?? '',
        Location: vehicle.location || '',
        Hub: allHubs.find((hub) => hub.id === (vehicle as any).hubId)?.name || vehicle.location || '',
        'Last Updated': vehicle.lastUpdated ? new Date(vehicle.lastUpdated).toLocaleString() : '',
        'Operational Status Updated By': vehicle.operationalStatusUpdatedBy || '',
        'Operational Status Updated At': vehicle.operationalStatusUpdatedAt ? new Date(vehicle.operationalStatusUpdatedAt).toLocaleString() : '',
        Color: vehicle.color || '',
        OEM: vehicle.oem || '',
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Vehicle Inventory');
      XLSX.writeFile(workbook, `vehicle-inventory-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error('Vehicle export failed:', error);
      setSnackbar({
        open: true,
        message: 'Failed to export vehicle data',
        severity: 'error',
      });
    } finally {
      setExporting(false);
    }
  }, [allHubs, applyClientSideFilters, buildApiFilters, sortConfig.direction, sortConfig.key, transformVehicleRecord]);

  const handleOperationalStatusChange = async (
  vehicleId: string,
  newStatus: string
) => {
  try {
    setUpdatingVehicleId(vehicleId);
    const updatedBy = getCurrentUserDisplayName();
    const updatedAt = new Date().toISOString();

    // Optimistic UI update
    setVehicles((prev) =>
      prev.map((vehicle) =>
        vehicle.id === vehicleId
          ?{
  ...vehicle,
  rawOperationalStatus: newStatus,
  status: normalizeOperationalStatus(newStatus),

  // instantly update vehicle status UI
  vehicleStatus:
    newStatus.toLowerCase().includes('assigned')
      ? 'DEPLOYED'
      : 'RETURNED',
  operationalStatusUpdatedBy: updatedBy,
  operationalStatusUpdatedAt: updatedAt,
  lastUpdated: updatedAt,
}
          : vehicle
      )
    );

    await vehicleService.updateVehicleStatus(
      vehicleId,
      newStatus,
      "Updated from inventory dashboard",
      updatedBy
    );

    setSnackbar({
      open: true,
      message: "Vehicle status updated successfully",
      severity: "success",
    });
  } catch (error) {
    console.error("Status update failed:", error);

    setSnackbar({
      open: true,
      message: "Failed to update vehicle status",
      severity: "error",
    });

    // Reload old data if failed
    loadVehicles(false);
  } finally {
    setUpdatingVehicleId(null);
  }
};


  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Helper functions
  // const getBatteryIcon = (level?: number) => {
  //   if (!level) return BatteryLowIcon;
  //   if (level < 20) return BatteryLowIcon;
  //   if (level < 80) return BatteryGoodIcon;
  //   return BatteryFullIcon;
  // };

  // const getBatteryColor = (level?: number) => {
  //   if (!level || level < 20) return 'error';
  //   if (level < 50) return 'warning';
  //   return 'success';
  // };

  // Only show locations (hubs) for selected city
 const getUniqueLocations = useMemo(() => {
  // Removed city-based location filtering
  return [];
}, []);

  const getUniqueVehicleStatuses = useMemo(() => {
    return [...new Set(vehicles.map((v: any) => v.vehicleStatus).filter(Boolean))];
  }, [vehicles]);

  // Log unique cities and vehicle statuses when the drawer opens (debug)
  useEffect(() => {
    if (filterDrawerOpen) {
      // const cities = [...new Set(vehicles.map((v: any) => v.city).filter(Boolean))];
      const statuses = [...new Set(vehicles.map((v: any) => v.vehicleStatus).filter(Boolean))];
      console.log('🔎 Filter Drawer opened. VehicleStatuses:', statuses);
    }
  }, [filterDrawerOpen, vehicles]);

  const getUniqueOEMs = useMemo(() => {
    return [...new Set(vehicles.map(v => v.oem).filter(Boolean))];
  }, [vehicles]);

  const getUniqueYears = useMemo(() => {
    return [...new Set(vehicles.map(v => v.year))].sort((a, b) => b - a);
  }, [vehicles]);


  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="40%" height={60} sx={{ mb: 3 }} />
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[...Array(5)].map((_, i) => (
            <Grid item xs={12} sm={6} md={2.4} key={i}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Skeleton variant="text" width="60%" height={40} />
                  <Skeleton variant="text" width="80%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Card>
          <CardContent>
            <Skeleton variant="text" width="100%" height={60} />
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="text" width="100%" height={60} sx={{ my: 1 }} />
            ))}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Fade in={true}>
      <Box className="mobile-page vehicle-inventory-page" sx={{ p: 3, maxWidth: '100%', overflow: 'hidden' }}>
        {/* Enhanced Header */}
        <Box className="mobile-page-header" sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Vehicle Inventory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and monitor your vehicle fleet
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' }, width: { xs: '100%', md: 'auto' } }}>
            <Tooltip title={refreshing ? "Refreshing..." : "Refresh data"}>
              <IconButton onClick={handleRefresh} disabled={refreshing}>
                <RefreshIcon className={refreshing ? 'rotating' : ''} />
              </IconButton>
            </Tooltip>

            {hasCreatePermission('vehicle', 'vehicles') && (
              <>
                <Button
                  variant="outlined"
                  startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                  onClick={handleExportVehicles}
                  disabled={exporting || loading}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {exporting ? 'Exporting...' : 'Export'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ReportIcon />}
                  onClick={() => navigate('/vehicles/status-backup')}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Status Backup
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => navigate('/vehicles/bulk-import')}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Import
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PendingIcon />}
                  onClick={handleDeploymentTicketsToggle}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Deployment Tickets
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PendingIcon />}
                  onClick={handleReturnTicketsToggle}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Return Tickets
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddVehicle}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Add Vehicle
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Enhanced Stats Cards */}
        {/* {stats && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" color="primary.main">
                        {stats.total}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Vehicles
                      </Typography>
                    </Box>
                    <BikeIcon color="primary" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" color="success.main">
                        {stats.active}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active
                      </Typography>
                    </Box>
                    <SuccessIcon color="success" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" color="warning.main">
                        {stats.maintenance}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Maintenance
                      </Typography>
                    </Box>
                    <WarningIcon color="warning" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" color="info.main">
                        {stats.inactive}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inactive
                      </Typography>
                    </Box>
                    <PendingIcon color="info" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )} */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Vehicles', value: summaryStats.total, color: 'primary.main', helper: 'Filtered fleet count' },
            { label: 'Deployed', value: summaryStats.deployed, color: 'success.main', helper: 'Assigned vehicles' },
            { label: 'Returned', value: summaryStats.returned, color: 'secondary.main', helper: 'Returned fleet' },
            { label: 'RTD', value: summaryStats.rtd, color: 'info.main', helper: 'Ready to deploy' },
          ].map((stat) => (
            <Grid item xs={12} sm={6} md={3} key={stat.label}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    {stat.label}
                  </Typography>
                  <Typography variant="h4" sx={{ color: stat.color, fontWeight: 900, lineHeight: 1.15, mt: 0.5 }}>
                    {summaryLoading && !summaryInitialized ? <Skeleton width={72} /> : stat.value.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.helper}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Enhanced Search and Quick Filters */}
        <Card className="mobile-filter-card" sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'minmax(320px, 1.7fr) minmax(190px, 0.9fr) minmax(160px, 0.75fr) minmax(160px, 0.75fr) minmax(160px, 0.75fr) auto',
                },
                gap: 1.25,
                alignItems: 'center',
              }}
            >
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Search vehicles by VIN, make, model, registration..."
                  value={searchInputValue}
                  onChange={handleSearchInputChange}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleApplySearch();
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchInputValue && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchInputValue('');
                            setSearchQuery('');
                            setPage(0);
                          }}
                        >
                          <ClearIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleApplySearch}
                          aria-label="Search vehicles"
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Operational Status</InputLabel>
                  <Select
                    size="small"
                    value={filters.status || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Operational Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    {OPERATIONAL_STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Vehicle Status</InputLabel>
                  <Select
                    size="small"
                    value={filters.vehicleStatus || ''}
                    onChange={(e) => handleFilterChange('vehicleStatus', e.target.value)}
                    label="Vehicle Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    {(getUniqueVehicleStatuses.length ? getUniqueVehicleStatuses : ['DEPLOYED','RETURNED']).map((vs) => (
                      <MenuItem key={vs} value={vs}>{vs}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                  {/* <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Hub</InputLabel>
                    <Select
                      size="small"
                      value={filters.hubId || ''}
                      onChange={(e) => handleFilterChange('hubId', e.target.value)}
                      label="Hub"
                    >
                      <MenuItem value="">All</MenuItem>
                      {allHubs.length ? (
                        allHubs.map((hub) => (
                          <MenuItem key={hub.id} value={hub.id}>{hub.name}</MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>Loading hubs...</MenuItem>
                      )}
                    </Select>
                  </FormControl> */}

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: 'contents' }}>
                    <DatePicker
                      label="Start Date"
                      value={parseDateKey(filters.updatedStartDate)}
                      format="dd-MM-yyyy"
                      onChange={(value) => handleFilterChange('updatedStartDate', toDateKey(value) || '')}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                        },
                      }}
                    />

                    <DatePicker
                      label="End Date"
                      value={parseDateKey(filters.updatedEndDate)}
                      format="dd-MM-yyyy"
                      onChange={(value) => handleFilterChange('updatedEndDate', toDateKey(value) || '')}
                      minDate={parseDateKey(filters.updatedStartDate) || undefined}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>

                <Button
                  size="medium"
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{ minHeight: 40, px: 2 }}
                >
                  Clear
                </Button>
            </Box>

          <Box
  className="mobile-hub-filter-row"
  sx={{
    mt: 1.5,
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexWrap: "wrap",
  }}
>
  <LocationIcon
    sx={{
      fontSize: 18,
      color: "success.main",
    }}
  />

  <Button
    size="small"
    variant={!filters.hubId ? "contained" : "outlined"}
    onClick={() => handleFilterChange("hubId", "")}
    sx={{
      borderRadius: "18px",
      minHeight: 32,
      textTransform: "none",
    }}
  >
    All Hubs
  </Button>

  {allHubs.map((hub) => (
    <Button
      key={hub.id}
      size="small"
      variant={
        filters.hubId === hub.id
          ? "contained"
          : "outlined"
      }
      onClick={() => handleFilterChange("hubId", hub.id)}
      sx={{
        borderRadius: "18px",
        minHeight: 32,
        textTransform: "none",
      }}
    >
      {hub.name}
    </Button>
  ))}
</Box>

            {(Object.keys(filters).length > 0 || Object.values(quickFilters).some(Boolean)) && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  Active filters:
                </Typography>
                {Object.entries(filters).map(([key, value]) => value && (
                  <Chip
                    key={key}
                    label={`${key.replace(/([A-Z])/g, ' $1')}: ${value}`}
                    size="small"
                    variant="outlined"
                    onDelete={() => handleFilterChange(key as keyof VehicleFilters, '')}
                  />
                ))}
                {Object.entries(quickFilters).map(([key, value]) => value && (
                  <Chip
                    key={key}
                    label={key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                    size="small"
                    variant="outlined"
                    onDelete={() => handleQuickFilterChange(key as keyof typeof quickFilters, false)}
                  />
                ))}
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                >
                  Clear All
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Mobile Vehicle Cards */}
        <Box className="vehicle-mobile-card-list" sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
          {totalVehicles === 0 ? (
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No vehicles found
              </Typography>
            </Card>
          ) : (
            <Stack spacing={1.25}>
              {displayedVehicles.map((vehicle) => (
                <Card
                  key={vehicle.id}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Avatar
                        onClick={() => handleViewVehicle(vehicle.id)}
                        sx={{
                          bgcolor: 'primary.main',
                          width: 44,
                          height: 44,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        <BikeIcon fontSize="small" />
                      </Avatar>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={800}
                              noWrap
                              onClick={() => handleViewVehicle(vehicle.id)}
                              sx={{ cursor: 'pointer' }}
                            >
                              {vehicle.registrationNumber || 'No Registration'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {vehicle.make} {vehicle.model?.displayName} - {vehicle.year}
                            </Typography>
                          </Box>
                          {vehicle.vehicleStatus ? (
                            <Chip
                              label={vehicle.vehicleStatus}
                              color={vehicle.vehicleStatus === 'DEPLOYED' ? 'success' : vehicle.vehicleStatus === 'RETURNED' ? 'secondary' : 'default'}
                              size="small"
                              sx={{ fontWeight: 700, maxWidth: 112 }}
                            />
                          ) : (
                            <Chip label="N/A" size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                          )}
                        </Stack>

                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          {[
                            ['VIN', vehicle.vin || '-'],
                            ['Location', vehicle.location || 'Unknown'],
                            ['Range', vehicle.range ? `${vehicle.range} km` : 'N/A'],
                            ['Updated', vehicle.lastUpdated ? new Date(vehicle.lastUpdated).toLocaleDateString() : 'N/A'],
                            ['Updated By', vehicle.operationalStatusUpdatedBy || 'N/A'],
                          ].map(([label, value]) => (
                            <Grid item xs={6} key={label}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
                                {label}
                              </Typography>
                              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                {value}
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>

                        <Box sx={{ mt: 1.25 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                            Operational Status
                          </Typography>
                          <FormControl fullWidth size="small">
                            <Select
                              value={vehicle.rawOperationalStatus || ''}
                              onChange={(e) => handleOperationalStatusChange(vehicle.id, e.target.value)}
                              disabled={updatingVehicleId === vehicle.id || vehicle.vehicleStatus === 'DEPLOYED'}
                            >
                              <MenuItem value="Available">Available</MenuItem>
                              <MenuItem value="Assigned">Assigned</MenuItem>
                              <MenuItem value="Assigned - Team Use">Assigned - Team Use</MenuItem>
                              <MenuItem value="Returned">Returned</MenuItem>
                              <MenuItem value="Available - Without(Battery & Charger)">Available - Without(Battery & Charger)</MenuItem>
                              <MenuItem value="Under Preventive Maintenance">Under Preventive Maintenance</MenuItem>
                              <MenuItem value="Under Maintenance - Spare Issue">Under Maintenance - Spare Issue</MenuItem>
                              <MenuItem value="Under Maintenance - External Work">Under Maintenance - External Work</MenuItem>
                              <MenuItem value="Under Maintenance - IoT Failure">Under Maintenance - IoT Failure</MenuItem>
                              <MenuItem value="Under Maintenance - Accidental / Insurance Claim">Under Maintenance - Accidental / Insurance Claim</MenuItem>
                              <MenuItem value="Under Maintenance - Major Repair">Under Maintenance - Major Repair</MenuItem>
                              <MenuItem value="Under Recovery">Under Recovery</MenuItem>
                              <MenuItem value="Under Police Custody / FIR">Under Police Custody / FIR</MenuItem>
                              <MenuItem value="Under Warranty Claim">Under Warranty Claim</MenuItem>
                              <MenuItem value="Under PDI">Under PDI</MenuItem>
                              <MenuItem value="Asset Missing">Asset Missing</MenuItem>
                              <MenuItem value="To Be Scrapped / Returned to Lessor">To Be Scrapped / Returned to Lessor</MenuItem>
                              <MenuItem value="Scrapped / Returned to Lessor">Scrapped / Returned to Lessor</MenuItem>
                              <MenuItem value="Retired">Retired</MenuItem>
                              <MenuItem value="Damaged">Damaged</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 1 }}>
                          <IconButton size="small" onClick={() => handleViewVehicle(vehicle.id)} aria-label="View vehicle">
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          {hasUpdatePermission('vehicle', 'vehicles') && (
                            <IconButton size="small" onClick={() => handleEditVehicle(vehicle.id)} aria-label="Edit vehicle">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          {hasDeletePermission('vehicle', 'vehicles') && (
                            <IconButton size="small" color="error" onClick={() => handleArchiveVehicle(vehicle.id)} aria-label="Archive vehicle">
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}

          {totalVehicles > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalVehicles}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
        </Box>

        {/* Enhanced Vehicles Table */}
        <Paper className="vehicle-desktop-table" variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { py: 1, px: 1.5 } }}>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'vin'}
                      direction={sortConfig.key === 'vin' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('vin')}
                    >
                      VIN
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'status'}
                      direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Operational Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'vehicleStatus'}
                      direction={sortConfig.key === 'vehicleStatus' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('vehicleStatus' as keyof Vehicle)}
                    >
                      Vehicle Status
                    </TableSortLabel>
                  </TableCell>
                  {/* <TableCell>Battery</TableCell> */}
                  <TableCell>Range</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedVehicles.map((vehicle) => {
                  // const BatteryIcon = getBatteryIcon(vehicle.batteryLevel);

                  return (
                    <TableRow key={vehicle.id} hover sx={{ cursor: 'pointer', '& td': { py: 1, px: 1.5 } }}>
                      <TableCell onClick={() => handleViewVehicle(vehicle.id)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
                            <BikeIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {vehicle.registrationNumber || 'No Registration'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {vehicle.make} {vehicle.model?.displayName} • {vehicle.year}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.875rem' }}>
                          {vehicle.vin}
                        </Typography>
                      </TableCell>

                      <TableCell>
  <FormControl fullWidth size="small">
    <Select
      value={vehicle.rawOperationalStatus || ''}
      onChange={(e) =>
        handleOperationalStatusChange(
          vehicle.id,
          e.target.value
        )
      }
      disabled={
  updatingVehicleId === vehicle.id ||
  vehicle.vehicleStatus === 'DEPLOYED'
}
      sx={{
  minWidth: 220,
  maxWidth: 260,
}}
    >
      <MenuItem value="Available">Available</MenuItem>

      <MenuItem value="Assigned" disabled>
        Assigned
      </MenuItem>

      <MenuItem value="Assigned - Team Use">
        Assigned - Team Use
      </MenuItem>

      <MenuItem value="Returned">
        Returned
      </MenuItem>

      <MenuItem value="Available - Without(Battery & Charger)">
        Available - Without(Battery & Charger)
      </MenuItem>

      <MenuItem value="Under Preventive Maintenance">
        Under Preventive Maintenance
      </MenuItem>

      <MenuItem value="Under Maintenance - Spare Issue">
        Under Maintenance - Spare Issue
      </MenuItem>

      <MenuItem value="Under Maintenance - External Work">
        Under Maintenance - External Work
      </MenuItem>

      <MenuItem value="Under Maintenance - IoT Failure">
        Under Maintenance - IoT Failure
      </MenuItem>

      <MenuItem value="Under Maintenance - Accidental / Insurance Claim">
        Under Maintenance - Accidental / Insurance Claim
      </MenuItem>

      <MenuItem value="Under Maintenance - Major Repair">
        Under Maintenance - Major Repair
      </MenuItem>

      <MenuItem value="Under Recovery">
        Under Recovery
      </MenuItem>

      <MenuItem value="Under Police Custody / FIR">
        Under Police Custody / FIR
      </MenuItem>

      <MenuItem value="Under Warranty Claim">
        Under Warranty Claim
      </MenuItem>

      <MenuItem value="Under PDI">
        Under PDI
      </MenuItem>

      <MenuItem value="Asset Missing">
        Asset Missing
      </MenuItem>

      <MenuItem value="To Be Scrapped / Returned to Lessor">
        To Be Scrapped / Returned to Lessor
      </MenuItem>

      <MenuItem value="Scrapped / Returned to Lessor">
        Scrapped / Returned to Lessor
      </MenuItem>

      <MenuItem value="Retired">
        Retired
      </MenuItem>

      <MenuItem value="Damaged">
        Damaged
      </MenuItem>
    </Select>
  </FormControl>
</TableCell>

                      <TableCell>
                        {vehicle.vehicleStatus ? (
                          <Chip
                            label={vehicle.vehicleStatus}
                            color={vehicle.vehicleStatus === 'DEPLOYED' ? 'success' : vehicle.vehicleStatus === 'RETURNED' ? 'secondary' : 'default'}
                            size="small"
                            variant="filled"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">N/A</Typography>
                        )}
                      </TableCell>

                      {/* <TableCell>
                        {vehicle.batteryLevel !== undefined ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BatteryIcon color={getBatteryColor(vehicle.batteryLevel)} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {vehicle.batteryLevel}%
                            </Typography>
                            <Box sx={{ width: 50, ml: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={vehicle.batteryLevel}
                                color={getBatteryColor(vehicle.batteryLevel)}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell> */}

                      <TableCell>
                        {vehicle.range ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SpeedIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {vehicle.range} km
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {vehicle.location || 'Unknown'}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        {vehicle.lastUpdated ? (
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(vehicle.lastUpdated).toLocaleDateString()}
                            </Typography>
                            {vehicle.operationalStatusUpdatedBy && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                                By: {vehicle.operationalStatusUpdatedBy}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewVehicle(vehicle.id);
                              }}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {hasUpdatePermission('vehicle', 'vehicles') && (
                            <Tooltip title="Edit Vehicle">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditVehicle(vehicle.id);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasDeletePermission('vehicle', 'vehicles') && (
                            <Tooltip title="Archive Vehicle">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchiveVehicle(vehicle.id);
                                }}
                              >
                                <ArchiveIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {totalVehicles > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalVehicles}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              showFirstButton
              showLastButton
            />
          )}

          {totalVehicles === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No vehicles found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totalVehicles === 0
                  ? "No vehicles have been added yet."
                  : "Try adjusting your search or filter criteria."
                }
              </Typography>
              {totalVehicles === 0 && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddVehicle}
                  sx={{ mt: 2 }}
                >
                  Add First Vehicle
                </Button>
              )}
            </Box>
          )}
        </Paper>

        {/* Filter Drawer */}
        <Drawer
          anchor="right"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{ sx: { width: 320, p: 2 } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Filters</Typography>
            <IconButton onClick={() => setFilterDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Use the dropdowns below to filter by <strong>Vehicle Status</strong>.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active (Available + Assigned)</MenuItem>
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="available - without(battery & charger)">Available - Without (Battery & Charger)</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="assigned - team use">Assigned - Team Use</MenuItem>
                <MenuItem value="returned">Returned</MenuItem>
                <MenuItem value="under preventive maintenance">Under Preventive Maintenance</MenuItem>
                <MenuItem value="under maintenance - spare issue">Under Maintenance - Spare Issue</MenuItem>
                <MenuItem value="under maintenance - extenal work">Under Maintenance - External Work</MenuItem>
                <MenuItem value="under maintenance - iot failure">Under Maintenance - IoT Failure</MenuItem>
                <MenuItem value="under maintenance - accidental / insurance claim">Under Maintenance - Accidental / Insurance Claim</MenuItem>
                <MenuItem value="under maintenance - major repair">Under Maintenance - Major Repair</MenuItem>
                <MenuItem value="under recovery">Under Recovery</MenuItem>
                <MenuItem value="under police custody / fir">Under Police Custody / FIR</MenuItem>
                <MenuItem value="under warranty claim">Under Warranty Claim</MenuItem>
                <MenuItem value="under pdi">Under PDI</MenuItem>
                <MenuItem value="asset missing">Asset Missing</MenuItem>
                <MenuItem value="to be scrapped / returned to lessor">To be Scrapped / Returned to Lessor</MenuItem>
                <MenuItem value="scrapped /  / returned to lessor">Scrapped / Returned to Lessor</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Vehicle Status</InputLabel>
              <Select
                value={filters.vehicleStatus || ''}
                onChange={(e) => handleFilterChange('vehicleStatus', e.target.value)}
                label="Vehicle Status"
              >
                <MenuItem value="">All</MenuItem>
                {(getUniqueVehicleStatuses.length ? getUniqueVehicleStatuses : ['DEPLOYED','RETURNED']).map((vs) => (
                  <MenuItem key={vs} value={vs}>
                    {vs}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Hub filter added */}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Hub</InputLabel>
              <Select
                value={filters.hubId || ''}
                onChange={(e) => handleFilterChange('hubId', e.target.value)}
                label="Hub"
              >
                <MenuItem value="">All</MenuItem>
                {allHubs.length ? (
                  allHubs.map((hub) => (
                    <MenuItem key={hub.id} value={hub.id}>{hub.name}</MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>Loading hubs...</MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>OEM</InputLabel>
              <Select
                value={filters.oem || ''}
                onChange={(e) => handleFilterChange('oem', e.target.value)}
                label="OEM"
              >
                <MenuItem value="">All</MenuItem>
                {getUniqueOEMs.map((oemName) => (
                  <MenuItem key={oemName} value={oemName}>
                    {oemName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              options={getUniqueYears}
              value={filters.year ? parseInt(filters.year) : null}
              onChange={(_, value) => handleFilterChange('year', value?.toString() || '')}
              renderInput={(params) => (
                <TextField {...params} label="Year" fullWidth />
              )}
            />

            <Autocomplete
              options={getUniqueLocations}
              value={filters.location || null}
              onChange={(_, value) => handleFilterChange('location', value || '')}
              renderInput={(params) => (
                <TextField {...params} label="Location" fullWidth />
              )}
            />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Registration Start Date"
                value={parseDateKey(filters.startDate)}
                format="dd-MM-yyyy"
                onChange={(value) => handleFilterChange('startDate', toDateKey(value) || '')}
                maxDate={parseDateKey(filters.endDate) || undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />

              <DatePicker
                label="Registration End Date"
                value={parseDateKey(filters.endDate)}
                format="dd-MM-yyyy"
                onChange={(value) => handleFilterChange('endDate', toDateKey(value) || '')}
                minDate={parseDateKey(filters.startDate) || undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />

              <DatePicker
                label="Updated Start Date"
                value={parseDateKey(filters.updatedStartDate)}
                format="dd-MM-yyyy"
                onChange={(value) => handleFilterChange('updatedStartDate', toDateKey(value) || '')}
                maxDate={parseDateKey(filters.updatedEndDate) || undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />

              <DatePicker
                label="Updated End Date"
                value={parseDateKey(filters.updatedEndDate)}
                format="dd-MM-yyyy"
                onChange={(value) => handleFilterChange('updatedEndDate', toDateKey(value) || '')}
                minDate={parseDateKey(filters.updatedStartDate) || undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>

            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
              >
                Clear All Filters
              </Button>
            </Box>
          </Box>
        </Drawer>

        {/* Floating fixed filter tab for consistent location */}
        {/* <Box sx={{ position: 'fixed', right: 20, bottom: 24, zIndex: 1400 }}>
          <Tooltip title="Filters">
            <IconButton
              onClick={() => setFilterDrawerOpen(true)}
              sx={{ bgcolor: 'background.paper', boxShadow: 3 }}
              aria-label="open filters"
            >
              <Badge badgeContent={activeFiltersCount} color="primary">
                <FilterIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box> */}

        {/* Enhanced Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* CSS for animations */}
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .rotating {
              animation: spin 1s linear infinite;
            }
          `}
        </style>
      </Box>
    </Fade>
  );
};

export default VehicleInventoryPage;
